import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
  Checkbox,
  Paper,
  IconButton,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Link as LinkIcon, 
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import API_BASE from '../apiBase';
import axios from 'axios';
import { getStoredUser } from '../utils/userStorage';

const ProjectManagementModal = ({ open, onClose, onProjectCreated = () => {} }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [projectUsage, setProjectUsage] = useState({ used: 0, allowed: 0 });
  const [unlinkedProjects, setUnlinkedProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectUserEmails, setNewProjectUserEmails] = useState('');
  const [newProjectRepositoryType, setNewProjectRepositoryType] = useState('github');
  const [newProjectGithubToken, setNewProjectGithubToken] = useState('');
  const [newProjectGitlabToken, setNewProjectGitlabToken] = useState('');
  const [newProjectBitbucketToken, setNewProjectBitbucketToken] = useState('');
  const [newProjectAzureOrganization, setNewProjectAzureOrganization] = useState('');
  const [newProjectAzureProject, setNewProjectAzureProject] = useState('');
  const [newProjectAzureToken, setNewProjectAzureToken] = useState('');
  const [newProjectAnalyzerType, setNewProjectAnalyzerType] = useState('openai');
  const [newProjectAnalyzerApiKey, setNewProjectAnalyzerApiKey] = useState('');
  const [newProjectReviewCommentMode, setNewProjectReviewCommentMode] = useState('inline');
  const [newProjectJiraEnabled, setNewProjectJiraEnabled] = useState(false);
  const [newProjectJiraUrl, setNewProjectJiraUrl] = useState('');
  const [newProjectJiraUser, setNewProjectJiraUser] = useState('');
  const [newProjectJiraToken, setNewProjectJiraToken] = useState('');
  const [newProjectAzureBoardsEnabled, setNewProjectAzureBoardsEnabled] = useState(false);
  const [newProjectAzureBoardsOrg, setNewProjectAzureBoardsOrg] = useState('');
  const [newProjectAzureBoardsProject, setNewProjectAzureBoardsProject] = useState('');
  const [newProjectAzureBoardsToken, setNewProjectAzureBoardsToken] = useState('');
  const [newProjectTrelloEnabled, setNewProjectTrelloEnabled] = useState(false);
  const [newProjectTrelloApiKey, setNewProjectTrelloApiKey] = useState('');
  const [newProjectTrelloToken, setNewProjectTrelloToken] = useState('');
  const [newProjectZohoSprintsEnabled, setNewProjectZohoSprintsEnabled] = useState(false);
  const [newProjectZohoSprintsProjectId, setNewProjectZohoSprintsProjectId] = useState('');
  const [newProjectZohoSprintsTeamId, setNewProjectZohoSprintsTeamId] = useState('');
  const [newProjectZohoSprintsClientId, setNewProjectZohoSprintsClientId] = useState('');
  const [newProjectZohoSprintsClientSecret, setNewProjectZohoSprintsClientSecret] = useState('');
  const [newProjectZohoSprintsGrantToken, setNewProjectZohoSprintsGrantToken] = useState('');
  const [newProjectZohoSprintsDomain, setNewProjectZohoSprintsDomain] = useState('zoho.in');
  const [zohoTeams, setZohoTeams] = useState([]);
  const [zohoProjects, setZohoProjects] = useState([]);
  const [zohoAccessToken, setZohoAccessToken] = useState('');
  const [zohoRefreshToken, setZohoRefreshToken] = useState('');
  const [zohoDiscoveryLoading, setZohoDiscoveryLoading] = useState(false);
  const [zohoDiscoveryError, setZohoDiscoveryError] = useState('');
  const [zohoProjectsLoading, setZohoProjectsLoading] = useState(false);
  const [newProjectBusinessContext, setNewProjectBusinessContext] = useState('');
  const newBcWordCount = newProjectBusinessContext.trim() ? newProjectBusinessContext.trim().split(/\s+/).length : 0;
  const newBcTooLong = newBcWordCount > 100;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [compliance, setCompliance] = useState(null);
  const [showCompliance, setShowCompliance] = useState(false);
  const [allowQdexLLM, setAllowQdexLLM] = useState(false);
  // Comment preferences (Critical fixed true)
  const [commentPrefs, setCommentPrefs] = useState({ critical: true, medium: true, low: false });

  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      Promise.all([fetchProjectUsage(), fetchUnlinkedProjects(), fetchCompliancePolicy(), fetchAllowQdexFlag()]).finally(() => {
        setInitialLoading(false);
      });
    }
  }, [open]);

  const fetchProjectUsage = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/project-subscription/usage`);
      setProjectUsage(data);
    } catch (error) {
      console.error('Failed to fetch project usage:', error);
    }
  };

  const fetchUnlinkedProjects = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/project-subscription/unlinked-projects`);
      setUnlinkedProjects(data);
    } catch (error) {
      console.error('Failed to fetch unlinked projects:', error);
    }
  };

  const normalizeCompliance = (c) => {
    if (!c) return null;
    const n = { ...c };
    if (n.retention_basis === undefined && n.retention_days !== undefined) {
      n.retention_basis = 'payment basis';
    }
    if (n.consent_source_default === undefined && n.consent_source !== undefined) {
      n.consent_source_default = n.consent_source;
    }
    return n;
  };

  const fetchCompliancePolicy = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/compliance/policy`);
      setCompliance(normalizeCompliance(data) || null);
    } catch (err) {
      // Non-blocking; leave compliance null
    }
  };

  const fetchAllowQdexFlag = async () => {
    try {
      const resp = await axios.get(`${API_BASE}/api/payments/current-subscription`);
      const plan = resp?.data?.plan;
      setAllowQdexLLM(!!(plan && plan.ai_provided_by_us === true));
    } catch (e) {
      setAllowQdexLLM(false);
    }
  };

  const handleProjectSelection = (projectId) => {
    setSelectedProjects(prev => {
      // If project is already selected, unselect it
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      }
      
      // If trying to select a new project, check if we're at the limit
      // Check if current usage + selected projects would exceed the limit
      if (projectUsage.used + prev.length >= projectUsage.allowed) {
        // Don't allow selection if at limit
        return prev;
      }
      
      // Add the project to selection
      return [...prev, projectId];
    });
  };



  const handleLinkProjects = async () => {
    if (selectedProjects.length === 0) return;
    
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/api/project-subscription/link-projects`, { project_ids: selectedProjects });
      setSuccess('Projects linked successfully');
      setSelectedProjects([]);
      fetchUnlinkedProjects();
      fetchProjectUsage();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to link projects');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    if (newBcTooLong) {
      setError('Business context must be at most 100 words.');
      return;
    }
    
    setLoading(true);
    try {
      const projectData = {
        name: newProjectName,
        description: newProjectDescription,
        business_context: newProjectBusinessContext,
        repository_type: newProjectRepositoryType,
        analyzer_type: newProjectAnalyzerType,
        jira_enabled: newProjectJiraEnabled || false,
        azure_boards_enabled: newProjectAzureBoardsEnabled || false,
        trello_enabled: newProjectTrelloEnabled || false,
        zoho_sprints_enabled: newProjectZohoSprintsEnabled || false,
        review_comment_mode: newProjectReviewCommentMode,
        user_emails: newProjectUserEmails.split(',').map(email => email.trim()).filter(email => {
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          return email && emailRegex.test(email);
        }),
        repository_config: {},
        analyzer_config: {},
        jira_config: {},
        azure_boards_config: {},
        trello_config: {},
        zoho_sprints_config: {},
      };

      // Set repository config based on type
      if (newProjectRepositoryType === 'github') {
        projectData.repository_config = { token: newProjectGithubToken };
      } else if (newProjectRepositoryType === 'gitlab') {
        projectData.repository_config = { token: newProjectGitlabToken };
      } else if (newProjectRepositoryType === 'bitbucket') {
        projectData.repository_config = { 
          token: newProjectBitbucketToken
        };
      } else if (newProjectRepositoryType === 'azure') {
        projectData.repository_config = { 
          token: newProjectAzureToken,
          organization: newProjectAzureOrganization,
          project: newProjectAzureProject 
        };
      }

      // Set analyzer config
      // Set analyzer config (QDEX requires no API key)
      if (newProjectAnalyzerType === 'qdex') {
        projectData.analyzer_config = {};
      } else {
        projectData.analyzer_config = { api_key: newProjectAnalyzerApiKey };
      }

      // Set Jira config if enabled
      if (newProjectJiraEnabled) {
        projectData.jira_config = {
          base_url: newProjectJiraUrl,
          username: newProjectJiraUser,
          api_token: newProjectJiraToken
        };
      }

      // Set Azure Boards config
      if (newProjectAzureBoardsEnabled) {
        projectData.azure_boards_config = {
          organization: newProjectAzureBoardsOrg,
          project: newProjectAzureBoardsProject,
          token: newProjectAzureBoardsToken
        };
      } else {
        // Ensure empty config is sent when disabled
        projectData.azure_boards_config = {};
      }

      // Set Trello config
      if (newProjectTrelloEnabled) {
        projectData.trello_config = {
          api_key: newProjectTrelloApiKey,
          token: newProjectTrelloToken
        };
      } else {
        // Ensure empty config is sent when disabled
        projectData.trello_config = {};
      }

      if (newProjectZohoSprintsEnabled) {
        const zohoConfig = {
          team_id: newProjectZohoSprintsTeamId,
          project_id: newProjectZohoSprintsProjectId,
          client_id: newProjectZohoSprintsClientId,
          client_secret: newProjectZohoSprintsClientSecret,
          domain: newProjectZohoSprintsDomain,
        };
        if (zohoAccessToken && zohoRefreshToken) {
          zohoConfig.access_token = zohoAccessToken;
          zohoConfig.refresh_token = zohoRefreshToken;
        } else {
          zohoConfig.grant_token = newProjectZohoSprintsGrantToken;
        }
        projectData.zoho_sprints_config = zohoConfig;
      } else {
        projectData.zoho_sprints_config = {};
      }

      // Include compliance if available (prefilled from policy, editable below)
      if (compliance) {
        projectData.compliance = {
          ...compliance,
          consent_source: compliance.consent_source || compliance.consent_source_default || compliance.consent_source,
        };
      }

      // Include control preferences from toggles (critical enforced true)
      projectData.control_preference = {
        critical: true,
        medium: !!commentPrefs.medium,
        low: !!commentPrefs.low,
      };

      const { data } = await axios.post(`${API_BASE}/api/project-subscription/create-project`, projectData);
      setSuccess('Project created successfully');
      onProjectCreated(data);
      resetForm();
      fetchProjectUsage();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to create project');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchZohoTeams = async () => {
    setZohoDiscoveryError('');
    setZohoDiscoveryLoading(true);
    setZohoTeams([]);
    setZohoProjects([]);
    setNewProjectZohoSprintsTeamId('');
    setNewProjectZohoSprintsProjectId('');
    setZohoAccessToken('');
    setZohoRefreshToken('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/zoho-sprints/discover-teams`, {
        client_id: newProjectZohoSprintsClientId,
        client_secret: newProjectZohoSprintsClientSecret,
        grant_token: newProjectZohoSprintsGrantToken,
        domain: newProjectZohoSprintsDomain,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setZohoTeams(res.data.teams || []);
      setZohoAccessToken(res.data.access_token || '');
      setZohoRefreshToken(res.data.refresh_token || '');
      if (!res.data.teams?.length) {
        setZohoDiscoveryError('No teams found in your Zoho Sprints account.');
      }
    } catch (err) {
      setZohoDiscoveryError(err.response?.data?.error || 'Failed to fetch teams. Check your credentials and try again.');
    } finally {
      setZohoDiscoveryLoading(false);
    }
  };

  const handleZohoTeamSelect = async (teamId) => {
    setNewProjectZohoSprintsTeamId(teamId);
    setNewProjectZohoSprintsProjectId('');
    setZohoProjects([]);
    if (!teamId || !zohoAccessToken) return;
    setZohoProjectsLoading(true);
    setZohoDiscoveryError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/zoho-sprints/discover-projects`, {
        access_token: zohoAccessToken,
        team_id: teamId,
        domain: newProjectZohoSprintsDomain,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setZohoProjects(res.data.projects || []);
      if (!res.data.projects?.length) {
        setZohoDiscoveryError('No projects found for this team.');
      }
    } catch (err) {
      setZohoDiscoveryError(err.response?.data?.error || 'Failed to fetch projects.');
    } finally {
      setZohoProjectsLoading(false);
    }
  };

  const resetForm = () => {
    setNewProjectName('');
    setNewProjectDescription('');
    setNewProjectUserEmails('');
    setNewProjectRepositoryType('github');
    setNewProjectGithubToken('');
    setNewProjectGitlabToken('');
    setNewProjectBitbucketToken('');
    setNewProjectAzureOrganization('');
    setNewProjectAzureProject('');
    setNewProjectAzureToken('');
    setNewProjectAnalyzerType('openai');
    setNewProjectAnalyzerApiKey('');
    setNewProjectReviewCommentMode('inline');
    setNewProjectJiraEnabled(false);
    setNewProjectJiraUrl('');
    setNewProjectJiraUser('');
    setNewProjectJiraToken('');
    setNewProjectAzureBoardsEnabled(false);
    setNewProjectAzureBoardsOrg('');
    setNewProjectAzureBoardsProject('');
    setNewProjectAzureBoardsToken('');
    setNewProjectTrelloEnabled(false);
    setNewProjectTrelloApiKey('');
    setNewProjectTrelloToken('');
    setNewProjectZohoSprintsEnabled(false);
    setNewProjectZohoSprintsProjectId('');
    setNewProjectZohoSprintsTeamId('');
    setNewProjectZohoSprintsClientId('');
    setNewProjectZohoSprintsClientSecret('');
    setNewProjectZohoSprintsGrantToken('');
    setNewProjectZohoSprintsDomain('zoho.in');
    setZohoTeams([]);
    setZohoProjects([]);
    setZohoAccessToken('');
    setZohoRefreshToken('');
    setZohoDiscoveryError('');
    setNewProjectBusinessContext('');
    setError('');
    setSuccess('');
    onClose();
  };

  const handleClose = () => {
    resetForm();
  };

  const getUsageColor = () => {
    const percentage = projectUsage.allowed > 0 ? (projectUsage.used / projectUsage.allowed) * 100 : 0;
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  // Validation function to check if create project form is valid
  const isCreateProjectFormValid = () => {
    // Check if project name is provided
    if (!newProjectName.trim()) return false;

    // Check repository configuration based on repository type
    let hasValidRepoConfig = false;
    switch (newProjectRepositoryType) {
      case 'github':
        hasValidRepoConfig = !!newProjectGithubToken.trim();
        break;
      case 'gitlab':
        hasValidRepoConfig = !!newProjectGitlabToken.trim();
        break;
      case 'bitbucket':
        hasValidRepoConfig = !!newProjectBitbucketToken.trim();
        break;
      case 'azure':
        hasValidRepoConfig = !!(newProjectAzureOrganization.trim() && newProjectAzureProject.trim() && newProjectAzureToken.trim());
        break;
      default:
        hasValidRepoConfig = false;
    }

    if (!hasValidRepoConfig) return false;

    // Check AI configuration
    if (newProjectAnalyzerType !== 'qdex') {
      if (!newProjectAnalyzerApiKey.trim()) return false;
    }

    // Check Jira configuration if Jira is enabled
    if (newProjectJiraEnabled) {
      if (!newProjectJiraUrl.trim() || !newProjectJiraUser.trim() || !newProjectJiraToken.trim()) {
        return false;
      }
    }

    // Check Azure Boards configuration if Azure Boards is enabled
    if (newProjectAzureBoardsEnabled) {
      if (!newProjectAzureBoardsOrg.trim() || !newProjectAzureBoardsProject.trim() || !newProjectAzureBoardsToken.trim()) {
        return false;
      }
    }

    // Check Trello configuration if Trello is enabled
    if (newProjectTrelloEnabled) {
      if (!newProjectTrelloApiKey.trim() || !newProjectTrelloToken.trim()) {
        return false;
      }
    }

    // Check Zoho Sprints configuration if enabled
    if (newProjectZohoSprintsEnabled) {
      if (!newProjectZohoSprintsClientId.trim() || !newProjectZohoSprintsClientSecret.trim() || !newProjectZohoSprintsGrantToken.trim() || !newProjectZohoSprintsTeamId.trim() || !newProjectZohoSprintsProjectId.trim()) {
        return false;
      }
    }

    // Enforce business_context limit
    if (newBcTooLong) return false;

    return true;
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            fontWeight: 600,
            fontSize: '1.25rem',
            color: '#1f1f1f',
            pb: 2,
            px: 3,
            pt: 3
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f1f1f', mb: 0.5 }}>
              {projectUsage.used} of {projectUsage.allowed} projects used
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#737373',
              '&:hover': {
                bgcolor: '#F1F5F9',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                m: 2, 
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' }
              }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setError('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                m: 2, 
                borderRadius: 2,
                '& .MuiAlert-icon': { alignItems: 'center' }
              }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => setSuccess('')}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          )}

          {initialLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 8 }}>
              <CircularProgress size={48} />
            </Box>
          ) : (
            <>
              <Box sx={{ px: 3, pt: 2 }}>
                <Tabs 
                  value={activeTab} 
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      minHeight: 48,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: 2,
                      mx: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: '#F1F5F9',
                        color: '#1f1f1f'
                      },
                      '&:not(.Mui-selected)': {
                        color: '#737373'
                      }
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none'
                    }
                  }}
                >
                  <Tab 
                    icon={<LinkIcon />} 
                    label="Link Existing Projects" 
                    disabled={!unlinkedProjects || unlinkedProjects.length === 0}
                    iconPosition="start"
                  />
                  <Tab 
                    icon={<AddIcon />} 
                    label="Create New Project" 
                    disabled={projectUsage.used >= projectUsage.allowed}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 3 }}>
                {activeTab === 0 && (
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#737373',
                        mb: 3,
                        fontSize: '0.875rem'
                      }}
                    >
                      Select projects to link to your subscription:
                    </Typography>
                    
                    {projectUsage.used + selectedProjects.length >= projectUsage.allowed && (
                      <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          You have reached the maximum number of projects allowed ({projectUsage.allowed}).
                          {projectUsage.allowed === 1 && " This is your trial plan."}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Please upgrade to a paid plan to add more projects.</strong>
                        </Typography>
                      </Alert>
                    )}
                    
                    {!unlinkedProjects || unlinkedProjects.length === 0 ? (
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 4, 
                          textAlign: 'center',
                          backgroundColor: '#f8fafc',
                          borderRadius: 2,
                          border: '1px dashed #cbd5e1'
                        }}
                      >
                        <WarningIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No Unlinked Projects
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {projectUsage.allowed === 1 
                            ? "You have already created your trial project. Please upgrade to a paid plan to create more projects."
                            : "All your projects are already linked to subscriptions."
                          }
                        </Typography>
                      </Paper>
                    ) : (
                      <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {unlinkedProjects && unlinkedProjects.map((project) => {
                          const isSelected = selectedProjects.includes(project.id);
                          const limitReached = projectUsage.used + selectedProjects.length >= projectUsage.allowed;
                          const isDisabled = limitReached && !isSelected;

                          return (
                            <Card 
                              key={project.id} 
                              elevation={0}
                              sx={{ 
                                borderRadius: 2,
                                border: isSelected ? '2px solid #1f1f1f' : '1px solid #E2E8F0',
                                mb: 2,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  borderColor: '#CBD5E1',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                }
                              }}
                            >
                              <CardContent sx={{ py: 2, px: 3 }}>
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleProjectSelection(project.id)}
                                    disabled={isDisabled}
                                    sx={{ 
                                      '&.Mui-checked': {
                                        color: '#1f1f1f'
                                      },
                                      '&.Mui-disabled': {
                                        color: 'rgba(0, 0, 0, 0.38)'
                                      }
                                    }}
                                  />
                                  <Box flex={1}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#1f1f1f' }}>
                                      {project.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#737373', mb: 1 }}>
                                      {project.description || 'No description provided'}
                                    </Typography>
                                    {project.repository_type && (
                                      <Chip 
                                        label={project.repository_type} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ 
                                          fontSize: '0.75rem',
                                          height: 24,
                                          '& .MuiChip-label': {
                                            px: 1
                                          }
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AddIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                          Create a new project for your subscription:
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'primary.main', 
                          textDecoration: 'underline', 
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.dark' }
                        }}
                        onClick={() => window.open('/setup', '_blank')}
                      >
                        Setup Guide
                      </Typography>
                    </Box>
                    
                    <Box component="form" noValidate autoComplete="off">
                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Project Details
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              name="name" 
                              label="Project Name" 
                              value={newProjectName} 
                              onChange={(e) => setNewProjectName(e.target.value)} 
                              fullWidth 
                              required 
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              name="user_emails" 
                              label="Additional User Emails" 
                              value={newProjectUserEmails} 
                              onChange={(e) => setNewProjectUserEmails(e.target.value)} 
                              fullWidth 
                              placeholder="email1@example.com, email2@example.com"
                              helperText={(() => {
                                const userInfo = getStoredUser() || {};
                                const currentUserEmail = userInfo.email || 'Not logged in';
                                return `Current user (${currentUserEmail}) is automatically included`;
                              })()}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField 
                              name="description" 
                              label="Project Description" 
                              value={newProjectDescription} 
                              onChange={(e) => setNewProjectDescription(e.target.value)} 
                              fullWidth 
                              multiline 
                              rows={3}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField 
                              name="business_context" 
                              label="Business Context" 
                              value={newProjectBusinessContext} 
                              onChange={(e) => setNewProjectBusinessContext(e.target.value)} 
                              fullWidth 
                              multiline 
                              rows={3}
                              error={newBcTooLong}
                              helperText={`Max 100 words (${newBcWordCount}/100)`}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Repository Configuration
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Repository Type</InputLabel>
                              <Select 
                                value={newProjectRepositoryType} 
                                onChange={(e) => setNewProjectRepositoryType(e.target.value)}
                                label="Repository Type"
                                sx={{
                                  borderRadius: 2
                                }}
                              >
                                <MenuItem value="github">GitHub</MenuItem>
                                <MenuItem value="gitlab">GitLab</MenuItem>
                                <MenuItem value="bitbucket">Bitbucket</MenuItem>
                                <MenuItem value="azure">Azure DevOps</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {newProjectRepositoryType === 'gitlab' && (
                            <Grid item xs={12} sm={6}>
                              <TextField 
                                name="gitlab_token" 
                                label="GitLab Personal Access Token" 
                                type="password" 
                                value={newProjectGitlabToken} 
                                onChange={(e) => setNewProjectGitlabToken(e.target.value)} 
                                fullWidth 
                                required 
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </Grid>
                          )}
                          
                          {newProjectRepositoryType === 'github' && (
                            <Grid item xs={12} sm={6}>
                              <TextField 
                                name="github_token" 
                                label="GitHub Personal Access Token" 
                                type="password" 
                                value={newProjectGithubToken} 
                                onChange={(e) => setNewProjectGithubToken(e.target.value)} 
                                fullWidth 
                                required 
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </Grid>
                          )}
                          
                          {newProjectRepositoryType === 'bitbucket' && (
                            <Grid item xs={12} sm={6}>
                              <TextField 
                                name="bitbucket_token" 
                                label="Bitbucket Access Token" 
                                type="password" 
                                value={newProjectBitbucketToken} 
                                onChange={(e) => setNewProjectBitbucketToken(e.target.value)} 
                                fullWidth 
                                required 
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                  }
                                }}
                              />
                            </Grid>
                          )}
                          
                          {newProjectRepositoryType === 'azure' && (
                            <>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_organization" 
                                  label="Azure Organization" 
                                  value={newProjectAzureOrganization} 
                                  onChange={(e) => setNewProjectAzureOrganization(e.target.value)} 
                                  fullWidth 
                                  required 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_project" 
                                  label="Azure Project" 
                                  value={newProjectAzureProject} 
                                  onChange={(e) => setNewProjectAzureProject(e.target.value)} 
                                  fullWidth 
                                  required 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_token" 
                                  label="Azure Personal Access Token" 
                                  type="password" 
                                  value={newProjectAzureToken} 
                                  onChange={(e) => setNewProjectAzureToken(e.target.value)} 
                                  fullWidth 
                                  required 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>

                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          AI Configuration
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>AI Provider</InputLabel>
                              <Select 
                                value={newProjectAnalyzerType} 
                                onChange={(e) => setNewProjectAnalyzerType(e.target.value)}
                                label="AI Provider"
                                sx={{
                                  borderRadius: 2
                                }}
                              >
                                {allowQdexLLM && <MenuItem value="qdex">QDEX</MenuItem>}
                                <MenuItem value="openai">OpenAI</MenuItem>
                                <MenuItem value="gemini">Google Gemini</MenuItem>
                                <MenuItem value="claude">Claude</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField 
                              name="analyzer_api_key" 
                              label={newProjectAnalyzerType === 'qdex' ? 'Analyzer API Key (not required for QDEX)' : newProjectAnalyzerType === 'openai' ? 'OpenAI API Key' : newProjectAnalyzerType === 'claude' ? 'Claude API Key' : 'Google API Key'} 
                              type="password" 
                              value={newProjectAnalyzerApiKey} 
                              onChange={(e) => setNewProjectAnalyzerApiKey(e.target.value)} 
                              fullWidth 
                              required={newProjectAnalyzerType !== 'qdex'}
                              disabled={newProjectAnalyzerType === 'qdex'}
                              placeholder={newProjectAnalyzerType === 'qdex' ? 'Not required' : 'Enter API key'}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel>Review Comment Mode</InputLabel>
                              <Select 
                                value={newProjectReviewCommentMode} 
                                onChange={(e) => setNewProjectReviewCommentMode(e.target.value)}
                                label="Review Comment Mode"
                                sx={{
                                  borderRadius: 2
                                }}
                              >
                                <MenuItem value="inline">Inline Comments</MenuItem>
                                <MenuItem value="summary">Summary Comments</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Paper>

                      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Jira Integration (Optional)
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={newProjectJiraEnabled} 
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewProjectJiraEnabled(true);
                                      setNewProjectAzureBoardsEnabled(false);
                                      setNewProjectTrelloEnabled(false);
                                      setNewProjectZohoSprintsEnabled(false);
                                    } else {
                                      setNewProjectJiraEnabled(false);
                                    }
                                  }}
                                  disabled={newProjectAzureBoardsEnabled || newProjectTrelloEnabled || newProjectZohoSprintsEnabled}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#667eea'
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#667eea'
                                    }
                                  }}
                                />
                              }
                              label="Enable Jira Integration" 
                            />
                            {(newProjectAzureBoardsEnabled || newProjectTrelloEnabled || newProjectZohoSprintsEnabled) && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                                Disable Azure Boards, Trello, and Zoho Sprints to enable Jira
                              </Typography>
                            )}
                          </Grid>
                          {newProjectJiraEnabled && (
                            <>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="jira_url" 
                                  label="Jira Base URL" 
                                  value={newProjectJiraUrl} 
                                  onChange={(e) => setNewProjectJiraUrl(e.target.value)} 
                                  fullWidth 
                                  required 
                                  placeholder="https://your-domain.atlassian.net"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="jira_user" 
                                  label="Jira Username/Email" 
                                  value={newProjectJiraUser} 
                                  onChange={(e) => setNewProjectJiraUser(e.target.value)} 
                                  fullWidth 
                                  required 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="jira_token" 
                                  label="Jira API Token" 
                                  type="password" 
                                  value={newProjectJiraToken} 
                                  onChange={(e) => setNewProjectJiraToken(e.target.value)} 
                                  fullWidth 
                                  required 
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>

                      {/* Azure Boards Integration */}
                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Azure Boards Integration (Optional)
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={newProjectAzureBoardsEnabled} 
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewProjectAzureBoardsEnabled(true);
                                      setNewProjectJiraEnabled(false);
                                      setNewProjectTrelloEnabled(false);
                                      setNewProjectZohoSprintsEnabled(false);
                                    } else {
                                      setNewProjectAzureBoardsEnabled(false);
                                    }
                                  }}
                                  disabled={newProjectJiraEnabled || newProjectTrelloEnabled || newProjectZohoSprintsEnabled}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#0078d4'
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#0078d4'
                                    }
                                  }}
                                />
                              }
                              label="Enable Azure Boards Integration" 
                            />
                            {(newProjectJiraEnabled || newProjectTrelloEnabled || newProjectZohoSprintsEnabled) && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                                Disable Jira, Trello, and Zoho Sprints to enable Azure Boards
                              </Typography>
                            )}
                          </Grid>
                          {newProjectAzureBoardsEnabled && (
                            <>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_boards_org" 
                                  label="Organization" 
                                  value={newProjectAzureBoardsOrg} 
                                  onChange={(e) => setNewProjectAzureBoardsOrg(e.target.value)} 
                                  fullWidth 
                                  required 
                                  placeholder="your-organization"
                                  helperText="Azure DevOps organization name"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_boards_project" 
                                  label="Project" 
                                  value={newProjectAzureBoardsProject} 
                                  onChange={(e) => setNewProjectAzureBoardsProject(e.target.value)} 
                                  fullWidth 
                                  required 
                                  placeholder="your-project"
                                  helperText="Azure DevOps project name"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField 
                                  name="azure_boards_token" 
                                  label="Personal Access Token (PAT)" 
                                  type="password" 
                                  value={newProjectAzureBoardsToken} 
                                  onChange={(e) => setNewProjectAzureBoardsToken(e.target.value)} 
                                  fullWidth 
                                  required 
                                  helperText="PAT with Work Items read access"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                  <Typography variant="body2">
                                    <strong>PR Title Format:</strong> Use <code>#123</code> in your PR title to link to Azure Boards work item #123.
                                    <br />
                                    Example: <code>#123 Implement user authentication</code>
                                  </Typography>
                                </Alert>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>

                      {/* Trello Integration */}
                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Trello Integration (Optional)
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={newProjectTrelloEnabled} 
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewProjectTrelloEnabled(true);
                                      setNewProjectJiraEnabled(false);
                                      setNewProjectAzureBoardsEnabled(false);
                                      setNewProjectZohoSprintsEnabled(false);
                                    } else {
                                      setNewProjectTrelloEnabled(false);
                                    }
                                  }}
                                  disabled={newProjectJiraEnabled || newProjectAzureBoardsEnabled || newProjectZohoSprintsEnabled}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#0079bf'
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#0079bf'
                                    }
                                  }}
                                />
                              }
                              label="Enable Trello Integration" 
                            />
                            {(newProjectJiraEnabled || newProjectAzureBoardsEnabled || newProjectZohoSprintsEnabled) && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                                Disable Jira, Azure Boards, and Zoho Sprints to enable Trello
                              </Typography>
                            )}
                          </Grid>
                          {newProjectTrelloEnabled && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="trello_api_key" 
                                  label="Trello API Key" 
                                  value={newProjectTrelloApiKey} 
                                  onChange={(e) => setNewProjectTrelloApiKey(e.target.value)} 
                                  fullWidth 
                                  required 
                                  placeholder="your-api-key"
                                  helperText="Get your API key from https://trello.com/app-key"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="trello_token" 
                                  label="Trello Token" 
                                  type="password" 
                                  value={newProjectTrelloToken} 
                                  onChange={(e) => setNewProjectTrelloToken(e.target.value)} 
                                  fullWidth 
                                  required 
                                  placeholder="your-token"
                                  helperText="Generate token from https://trello.com/app-key"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                  <Typography variant="body2">
                                    <strong>PR Title Format:</strong> Use Trello card ID or URL in your PR title to link to Trello cards.
                                    <br />
                                    Examples: <code>[TRELLO-abc12345]</code> or <code>https://trello.com/c/abc12345</code>
                                  </Typography>
                                </Alert>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>

                      {/* Zoho Sprints Integration */}
                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Zoho Sprints Integration (Optional)
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch 
                                  checked={newProjectZohoSprintsEnabled} 
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewProjectZohoSprintsEnabled(true);
                                      setNewProjectJiraEnabled(false);
                                      setNewProjectAzureBoardsEnabled(false);
                                      setNewProjectTrelloEnabled(false);
                                    } else {
                                      setNewProjectZohoSprintsEnabled(false);
                                    }
                                  }}
                                  disabled={newProjectJiraEnabled || newProjectAzureBoardsEnabled || newProjectTrelloEnabled}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#f9a825'
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#f9a825'
                                    }
                                  }}
                                />
                              }
                              label="Enable Zoho Sprints Integration" 
                            />
                            {(newProjectJiraEnabled || newProjectAzureBoardsEnabled || newProjectTrelloEnabled) && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
                                Disable Jira, Azure Boards, and Trello to enable Zoho Sprints
                              </Typography>
                            )}
                          </Grid>
                          {newProjectZohoSprintsEnabled && (
                            <>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="zoho_client_id" 
                                  label="OAuth Client ID" 
                                  value={newProjectZohoSprintsClientId} 
                                  onChange={(e) => setNewProjectZohoSprintsClientId(e.target.value)} 
                                  fullWidth 
                                  required
                                  placeholder="1000.XXXXXXXXXX.XXXXXXXXXX"
                                  helperText="From Zoho API Console (api-console.zoho.in)"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="zoho_client_secret" 
                                  label="OAuth Client Secret" 
                                  type="password"
                                  value={newProjectZohoSprintsClientSecret} 
                                  onChange={(e) => setNewProjectZohoSprintsClientSecret(e.target.value)} 
                                  fullWidth 
                                  required
                                  placeholder="your-client-secret"
                                  helperText="From Zoho API Console"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="zoho_grant_token" 
                                  label="Grant Token" 
                                  type="password"
                                  value={newProjectZohoSprintsGrantToken} 
                                  onChange={(e) => {
                                    setNewProjectZohoSprintsGrantToken(e.target.value);
                                    setZohoTeams([]);
                                    setZohoProjects([]);
                                    setNewProjectZohoSprintsTeamId('');
                                    setNewProjectZohoSprintsProjectId('');
                                    setZohoAccessToken('');
                                    setZohoRefreshToken('');
                                  }}
                                  fullWidth 
                                  required
                                  placeholder="1000.XXXXXXXXXX.XXXXXXXXXX"
                                  helperText="Generate from Self Client in Zoho API Console"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField 
                                  name="zoho_domain" 
                                  label="Zoho Domain" 
                                  value={newProjectZohoSprintsDomain} 
                                  onChange={(e) => setNewProjectZohoSprintsDomain(e.target.value)} 
                                  fullWidth 
                                  required
                                  placeholder="zoho.in"
                                  helperText="Your Zoho region: zoho.in, zoho.com, zoho.eu, etc."
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2
                                    }
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Button
                                  variant="contained"
                                  onClick={handleFetchZohoTeams}
                                  disabled={zohoDiscoveryLoading || !newProjectZohoSprintsClientId.trim() || !newProjectZohoSprintsClientSecret.trim() || !newProjectZohoSprintsGrantToken.trim()}
                                  sx={{ 
                                    borderRadius: 2, 
                                    textTransform: 'none',
                                    bgcolor: '#f9a825',
                                    '&:hover': { bgcolor: '#f57f17' },
                                  }}
                                >
                                  {zohoDiscoveryLoading ? <><CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} /> Fetching Teams...</> : 'Fetch Teams & Projects'}
                                </Button>
                                {zohoDiscoveryError && (
                                  <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                                    {zohoDiscoveryError}
                                  </Alert>
                                )}
                              </Grid>

                              {zohoTeams.length > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth required>
                                    <InputLabel>Team (Portal)</InputLabel>
                                    <Select
                                      value={newProjectZohoSprintsTeamId}
                                      label="Team (Portal)"
                                      onChange={(e) => handleZohoTeamSelect(e.target.value)}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      {zohoTeams.map((t) => (
                                        <MenuItem key={t.id} value={t.id}>{t.name} ({t.id})</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}

                              {zohoProjectsLoading && (
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 56 }}>
                                    <CircularProgress size={20} />
                                    <Typography variant="body2" color="text.secondary">Loading projects...</Typography>
                                  </Box>
                                </Grid>
                              )}

                              {zohoProjects.length > 0 && !zohoProjectsLoading && (
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth required>
                                    <InputLabel>Project</InputLabel>
                                    <Select
                                      value={newProjectZohoSprintsProjectId}
                                      label="Project"
                                      onChange={(e) => setNewProjectZohoSprintsProjectId(e.target.value)}
                                      sx={{ borderRadius: 2 }}
                                    >
                                      {zohoProjects.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>{p.name || p.id} ({p.id})</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}

                              <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                  <Typography variant="body2">
                                    <strong>1.</strong> Enter Client ID, Client Secret, and Grant Token above, then click <strong>Fetch Teams & Projects</strong>.
                                    <br />
                                    <strong>2.</strong> Select your team and project from the dropdowns.
                                    <br />
                                    <strong>3.</strong> See the <strong><a href="/setup" target="_blank" rel="noopener noreferrer">Setup Guide → Zoho Sprints</a></strong> tab for how to get credentials.
                                    <br /><br />
                                    <strong>PR Title Format:</strong>{' '}
                                    <code>[ZOHO-3]</code>, <code>P1#I-3</code>, or <code>QDE-I3</code>
                                  </Typography>
                                </Alert>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Paper>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="outlined" size="small" onClick={() => setShowCompliance(prev => !prev)}>
                          {showCompliance ? 'Hide compliance data' : 'Check compliance data'}
                        </Button>
                      </Box>

                      {showCompliance && (
                        <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                          <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                            Compliance
                          </Typography>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                name="data_region"
                                label="Data Region (editable)"
                                value={compliance?.data_region || ''}
                                onChange={(e) => setCompliance(prev => ({ ...(prev || {}), data_region: e.target.value }))}
                                fullWidth
                                placeholder="e.g., US, EU, India"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fffde7',
                                    borderRadius: 1,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Retention Basis" value={compliance?.retention_basis ?? ''} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Lawful Basis" value={compliance?.lawful_basis ?? ''} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Consent Required" value={compliance?.consent_required ? 'Yes' : 'No'} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="Consent Source Default" value={compliance?.consent_source_default ?? ''} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="Purposes" value={(compliance?.purposes || []).join(', ')} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField label="PII Categories" value={(compliance?.pii_categories || []).join(', ')} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="DSR Contact" value={compliance?.dsr_contact ?? ''} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Anonymize On Export" value={compliance?.anonymize_on_export ? 'Yes' : 'No'} fullWidth InputProps={{ readOnly: true }} />
                            </Grid>
                          </Grid>
                        </Paper>
                      )}

                      {/* Comments Preferences */}
                      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
                          Comments
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                          Limit code review comments to selected severities. Critical is always included.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <FormControlLabel 
                              control={<Switch checked={true} disabled />}
                              label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Critical</Typography>
                                <Chip label="Required" size="small" sx={{ backgroundColor: '#FEE2E2', color: '#991B1B', height: 20 }} />
                              </Box>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControlLabel 
                              control={<Switch checked={commentPrefs.medium} onChange={(e) => setCommentPrefs(prev => ({ ...prev, medium: e.target.checked }))} />}
                              label={<Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Medium</Typography>}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <FormControlLabel 
                              control={<Switch checked={commentPrefs.low} onChange={(e) => setCommentPrefs(prev => ({ ...prev, low: e.target.checked }))} />}
                              label={<Typography sx={{ fontWeight: 600, color: '#1F2937' }}>Low</Typography>}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: '24px', gap: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              fontSize: '0.875rem',
              textTransform: 'none',
              color: '#737373',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                bgcolor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          
          {activeTab === 0 && (
            <Button
              onClick={handleLinkProjects}
              variant="contained"
              disabled={loading || selectedProjects.length === 0}
              startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                bgcolor: selectedProjects.length === 0 ? '#F1F5F9' : '#1f1f1f',
                color: selectedProjects.length === 0 ? '#94A3B8' : '#fff',
                '&:hover': {
                  bgcolor: selectedProjects.length === 0 ? '#F1F5F9' : '#0F172A',
                },
                '&:disabled': {
                  bgcolor: '#F1F5F9',
                  color: '#94A3B8'
                }
              }}
            >
              {loading ? 'Linking...' : `Link ${selectedProjects.length} Project${selectedProjects.length !== 1 ? 's' : ''}`}
            </Button>
          )}
          
          {activeTab === 1 && (
            <Button
              onClick={handleCreateProject}
              variant="contained"
              disabled={loading || !isCreateProjectFormValid()}
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                bgcolor: '#1f1f1f',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#0F172A',
                },
                '&:disabled': {
                  bgcolor: '#F1F5F9',
                  color: '#94A3B8'
                }
              }}
            >
              {loading ? 'Creating...' : 'Create Project'}
            </Button>
          )}
        </DialogActions>
      </Dialog>


    </>
  );
};

export default ProjectManagementModal; 